import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { ChevronRight, Eye, List } from 'lucide-react';
import { useTheme, ThemedCard, ThemedButton, ThemedContainer } from './ThemeContext';

const SpectatorMode = () => {
  const { woodStyles } = useTheme();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [activeGames, setActiveGames] = useState([]);
  const [currentGameIndex, setCurrentGameIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefreshTime, setLastRefreshTime] = useState(null);

  // Function to fetch all active games
  const fetchActiveGames = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get('/api/games/active');

      // Filter to only include games that are in active status
      const games = response.data.filter(game => game.status === 'active');

      setActiveGames(games);
      setLastRefreshTime(new Date());

      // Reset current index if out of bounds
      if (currentGameIndex >= games.length) {
        setCurrentGameIndex(0);
      }

      setError(null);
    } catch (err) {
      console.error('Error fetching active games:', err);
      setError('Failed to load active games. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [currentGameIndex]);

  // Initial data fetch
  useEffect(() => {
    fetchActiveGames();

    // Set up a refresh interval
    const intervalId = setInterval(fetchActiveGames, 30000); // refresh every 30 seconds

    return () => clearInterval(intervalId);
  }, [fetchActiveGames]);

  // Navigate to next random game
  const goToNextGame = () => {
    if (activeGames.length === 0) return;

    const nextIndex = (currentGameIndex + 1) % activeGames.length;
    setCurrentGameIndex(nextIndex);

    // Navigate to the new game
    navigate(`/game/${activeGames[nextIndex].id}`);
  };

  // Navigate to a specific game
  const spectateGame = (gameId) => {
    navigate(`/game/${gameId}`);
  };

  // Effect to auto-navigate to a game on initial load or when games change
  useEffect(() => {
    if (!isLoading && activeGames.length > 0 && !window.location.pathname.includes('/game/')) {
      // Only navigate if we're not already on a game page
      navigate(`/game/${activeGames[currentGameIndex].id}`);
    }
  }, [activeGames, isLoading, navigate, currentGameIndex]);

  if (isLoading && activeGames.length === 0) {
    return (
      <ThemedContainer className="max-w-6xl mx-auto p-4 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <div className={`text-lg ${woodStyles.text.primary}`}>Loading active games...</div>
        </div>
      </ThemedContainer>
    );
  }

  return (
    <ThemedContainer className="max-w-6xl mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className={`text-2xl font-bold ${woodStyles.text.primary}`}>
          Spectator Mode
        </h1>
        <div className="flex items-center gap-2">
          <ThemedButton
            onClick={fetchActiveGames}
            variant="secondary"
            className="flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </ThemedButton>
          <ThemedButton
            onClick={() => navigate('/lobby')}
            variant="secondary"
          >
            Back to Lobby
          </ThemedButton>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Active Game Controls */}
        <div className="lg:col-span-1">
          <ThemedCard>
            <div className={`px-4 py-3 border-b ${woodStyles.card.border}`}>
              <div className="flex justify-between items-center">
                <h2 className={`font-semibold ${woodStyles.text.primary}`}>
                  Active Games
                </h2>
                <div className={`text-sm px-2 py-1 rounded-full ${woodStyles.surface.secondary}`}>
                  <span className={woodStyles.text.primary}>
                    {activeGames.length}
                  </span>
                </div>
              </div>
            </div>

            <div className="p-4">
              {activeGames.length === 0 ? (
                <div className={`text-center p-6 ${woodStyles.text.secondary}`}>
                  No active games found
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <p className={`text-sm ${woodStyles.text.secondary}`}>
                      {activeGames.length} active games found
                    </p>
                    {lastRefreshTime && (
                      <p className={`text-xs ${woodStyles.text.secondary}`}>
                        Last updated: {lastRefreshTime.toLocaleTimeString()}
                      </p>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <ThemedButton
                      onClick={goToNextGame}
                      variant="primary"
                      className="w-full flex items-center justify-center gap-1"
                      disabled={activeGames.length === 0}
                    >
                      <ChevronRight className="w-5 h-5" />
                      Next Game
                    </ThemedButton>
                  </div>

                  <div className={`mt-4 text-sm ${woodStyles.text.secondary}`}>
                    {activeGames.length > 0 && (
                      <p>
                        Now viewing: Game {currentGameIndex + 1} of {activeGames.length}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </ThemedCard>

          {/* Game List */}
          <ThemedCard className="mt-4 overflow-hidden">
            <div className={`px-4 py-3 border-b ${woodStyles.card.border}`}>
              <h2 className={`font-semibold ${woodStyles.text.primary} flex items-center gap-2`}>
                <List className="w-4 h-4" />
                Game List
              </h2>
            </div>

            <div className="max-h-[400px] overflow-y-auto">
              {activeGames.map((game, index) => (
                <div
                  key={game.id}
                  className={`p-3 border-b ${woodStyles.card.border} ${index === currentGameIndex
                      ? woodStyles.surface.secondary
                      : 'hover:bg-opacity-50 hover:bg-gray-100 dark:hover:bg-gray-800'
                    } cursor-pointer transition-colors`}
                  onClick={() => {
                    setCurrentGameIndex(index);
                    spectateGame(game.id);
                  }}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <div className={`font-medium ${woodStyles.text.primary}`}>
                        Game #{game.id.slice(5)}
                      </div>
                      <div className={`text-xs ${woodStyles.text.secondary}`}>
                        {game.players.white?.username} vs {game.players.black?.username}
                      </div>
                    </div>
                    <div>
                      {index === currentGameIndex && (
                        <Eye className={`w-4 h-4 ${woodStyles.text.accent}`} />
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {activeGames.length === 0 && (
                <div className={`text-center p-6 ${woodStyles.text.secondary}`}>
                  No active games found
                </div>
              )}
            </div>
          </ThemedCard>
        </div>

        {/* Game Viewer */}
        <div className="lg:col-span-2">
          <ThemedCard className="h-full">
            <div className={`px-4 py-3 border-b ${woodStyles.card.border}`}>
              <h2 className={`font-semibold ${woodStyles.text.primary}`}>
                Game Viewer
              </h2>
            </div>

            <div className="p-4 h-[600px] flex items-center justify-center">
              <p className={`text-center ${woodStyles.text.secondary}`}>
                {isLoading ? (
                  <div className="animate-pulse">Loading game...</div>
                ) : activeGames.length > 0 ? (
                  <>Navigate to a game using the controls</>
                ) : (
                  <>No active games available to spectate</>
                )}
              </p>
            </div>
          </ThemedCard>
        </div>
      </div>
    </ThemedContainer>
  );
};

export default SpectatorMode;